import { Inject, Injectable, DOCUMENT, PLATFORM_ID } from '@angular/core';
import { map, take } from 'rxjs/operators';
import { ActivatedRouteSnapshot } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FormService } from 'src/sb-modules/Frontend-form-field-module/common/services/form.service';
import { FormCommonDataService } from 'src/sb-modules/Frontend-form-field-module/common/services/form-common-data.service';
import { LocalizationLanguage } from '@sb-tools/typescript/enums/localization-language.enum';
import { TranslateService } from '@ngx-translate/core';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { CommonUserService } from '@sb-tools/typescript/services/common-user.service';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { ClientForm } from 'src/sb-modules/Frontend-form-field-module/common/models/client-form.model';
import { environment } from '@environment';
import { SurveyService } from './survey.service';
import { Error } from '@sb-tools/typescript/enums/error.enum';
import { isPlatformServer } from '@angular/common';

@Injectable({
    providedIn: 'root',
})
export class FormDataResolverService {
    constructor(
        public formCommonDataService: FormCommonDataService,
        private bsLocaleService: BsLocaleService,
        private formService: FormService,
        private translateService: TranslateService,
        private commonUserService: CommonUserService,
        private commonDataService: CommonDataService,
        private surveyService: SurveyService,
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId: string,
    ) {}

    resolve(route: ActivatedRouteSnapshot): Observable<ClientForm> {
        if (isPlatformServer(this.platformId)) {
            return of(null);
        }
        this.formCommonDataService.isInternal = route.queryParams.isInternal === 'true';
        this.formCommonDataService.surveyToken = route.queryParams.token;

        environment.isNoNeedInCommunicator = !this.formCommonDataService.isInternal;
        if (this.formCommonDataService.surveyToken) {
            return this.surveyService.getForm(this.formCommonDataService.surveyToken).pipe(
                take(1),
                map((formInstance: ClientForm) => {
                    this.formCommonDataService.form = formInstance;
                    this.formCommonDataService.formName = this.formCommonDataService.form.name;
                    this.setupLanguage(formInstance.language);
                    return formInstance;
                }),
                catchError((err) => {
                    if (err.status === 400 && err.error.errorId === Error.invalidLinkSurveyAlreadyAnswered) {
                        this.formCommonDataService.isSurveySent = true;
                    }
                    return of(err);
                }),
            );
        } else if (this.formCommonDataService.isInternal) {
            return forkJoin({
                form: this.formService.get(route.params.formName),
                user: this.commonUserService.getFullInfo(),
            }).pipe(
                take(1),
                map(({ form, user }) => {
                    this.formCommonDataService.form = form;
                    this.formCommonDataService.formName = this.formCommonDataService.form.name;
                    this.commonDataService.user = user;
                    this.setupLanguage(
                        this.commonDataService.user.localizationLanguage || this.formCommonDataService.form.language,
                    );
                    return { form, user };
                }),
                catchError((err) => {
                    return of(err);
                }),
            );
        } else {
            return this.formService.get(route.params.formName).pipe(
                map((formInstance: ClientForm) => {
                    this.formCommonDataService.form = formInstance;
                    this.formCommonDataService.formName = this.formCommonDataService.form.name;
                    this.setupLanguage(formInstance.language);
                    return formInstance;
                }),
                catchError((err) => {
                    return of(err);
                }),
            );
        }
    }

    private setupLanguage(language: LocalizationLanguage): void {
        let locale: string;

        switch (language) {
            case LocalizationLanguage.norwegian:
                this.formCommonDataService.language = 'nb';
                locale = 'nb';
                this.translateService.use('nb');
                break;
            case LocalizationLanguage.swedish:
                this.formCommonDataService.language = 'sv_SE';
                locale = 'sv';
                this.translateService.use('sv_SE');
                break;
            case LocalizationLanguage.danish:
                this.formCommonDataService.language = 'da_DK';
                locale = 'da';
                this.translateService.use('da_DK');
                break;
            case LocalizationLanguage.finnish:
                this.formCommonDataService.language = 'fi';
                locale = 'fi';
                this.translateService.use('fi');
                break;
            default:
                this.formCommonDataService.language = 'en';
                locale = 'en';
                this.translateService.use(undefined);
        }

        this.document.documentElement.lang = locale;
        this.bsLocaleService.use(locale);
    }
}
